import * as React from "react";
import { ReactComponent as ChevronUp } from "./assets/chevron-up.svg";
import { ReactComponent as EllipseTop } from "./assets/ellipse-top.svg";
import { ReactComponent as EllipseBottom } from "./assets/ellipse-bottom.svg";
import { useFetchGoldenTicketSchools } from "../../../../networks";
import { getStaticImg } from "../../../../utils/function";
import analytics from "../../../../utils/analytics";
import {
  dashboardGoldenTicketFaqs,
  GOLDEN_TICKET_WA,
} from "../../../../data/goldenTicketFaq";
import {
  categoryParam,
  formatLastUpdated,
  formatWatermarkTime,
  toLeaderboardRows,
  watermarkBackground,
} from "./goldenTicket";

// Figma node 9159:1037 (dashboard, desktop 936) + node 9201:1551 (FAQ accordion,
// mobile 359). Warna & ukuran di sini diambil dari dua node itu.
const FAQS = dashboardGoldenTicketFaqs();

// Border di file ini sengaja pakai nilai arbitrary (`border-[1px]`, `border-[0px]`),
// bukan `border` / `border-0`: Bootstrap punya class bernama sama dengan !important
// (`.border` maksa warna abu-abu, `.border-0` ngapus semua sisi termasuk garis
// bawah baris), dan Bootstrap selalu menang.
const LeaderboardRow = ({ rank, schoolName, isQualified, isLast }) => (
  <div
    className={`flex items-center gap-[12px] px-[16px] py-[14px] ${
      isLast ? "" : "border-[0px] border-b-[1px] border-solid border-[#f0e0a3]"
    }`}
  >
    <p className="m-0 w-[40px] shrink-0 text-[13px] font-bold leading-[20px] text-[#1f2d3d] lg:w-[56px]">
      {rank}
    </p>
    {/* min-w-0: tanpa ini nama sekolah yang panjang bikin baris melar dan kolom
        status kedorong keluar kartu. */}
    <p className="m-0 min-w-0 flex-1 truncate text-[13px] font-bold leading-[20px] text-[#1f2d3d]">
      {schoolName}
    </p>
    {/* Semua baris dapat badge: yang lolos (is_qualified dari backend) "Golden
        Ticket", sisanya "Jalur Kurasi" — masih bisa lolos lewat kurasi, jadi
        warnanya netral, bukan kesan gagal. Kolom mobile 88px biar "Golden
        Ticket" muat satu baris. */}
    <div className="flex w-[88px] shrink-0 justify-center lg:w-[120px]">
      {isQualified ? (
        <span
          className={`whitespace-nowrap rounded-[4px] px-[4px] py-[2px] text-[11px] font-bold leading-[13px] bg-[#77b341] text-white`}
        >
          Golden Ticket
        </span>
      ) : null}
    </div>
  </div>
);

const LeaderboardHeader = () => (
  <div className="flex items-center gap-[12px] bg-gradient-to-b from-[#f2b61a] to-[#c97d33] px-[16px] py-[14px] text-[12px] leading-[16px] text-white">
    <p className="m-0 w-[40px] shrink-0 lg:w-[56px]">Rank</p>
    <p className="m-0 min-w-0 flex-1">Sekolah</p>
    <p className="m-0 w-[88px] shrink-0 text-center lg:w-[120px]">Status</p>
  </div>
);

// Header selalu dipasang, apa pun isinya: pas kosong pun kerangka tabelnya tetap
// kebaca sebagai leaderboard, bukan cuma sepotong pesan yang ngambang.
const Leaderboard = ({ isLoading, isError, rows, watermark }) => (
  <div className="overflow-hidden rounded-[16px] border-[1px] border-solid border-[#f2b61a] bg-white/85 shadow-[0px_8px_18px_-8px_rgba(201,166,43,0.08)]">
    <LeaderboardHeader />

    {/* Watermark ala Prisma Browser: lapisan di ATAS baris (bukan di belakang),
        jadi screenshot yang di-crop tetap kebawa. Cuma nimpa badan tabel — header
        gold-nya dibiarin bersih. pointer-events-none biar teks di bawahnya tetap
        bisa dipilih. Tile-nya di-center + badan tabel dikasih min-height biar
        tabel satu-dua baris tetap kebagian teks watermark yang utuh. */}
    <div
      className="relative"
      style={watermark ? { minHeight: watermark.textHeight } : undefined}
    >
      {watermark && (
        <div
          aria-hidden="true"
          data-testid="leaderboard-watermark"
          className="pointer-events-none absolute inset-0 z-[1] bg-center"
          style={{ backgroundImage: watermark.backgroundImage }}
        />
      )}

      {isLoading &&
        [0, 1, 2, 3, 4].map((row) => (
          <div
            key={row}
            className="flex items-center gap-[12px] border-[0px] border-b-[1px] border-solid border-[#f0e0a3] px-[16px] py-[14px]"
          >
            <div className="h-[12px] w-[24px] shrink-0 rounded-[4px] bg-[#f0e0a3]" />
            <div className="h-[12px] flex-1 rounded-[4px] bg-[#f0e0a3]" />
          </div>
        ))}

      {/* Gagal dimuat dan belum ada isi ditangani sama: yang berubah cuma kalimatnya. */}
      {!isLoading && (isError || !rows.length) && (
        <p className="m-0 px-[16px] py-[24px] text-center text-[13px] leading-[20px] text-[#6a7481]">
          {isError
            ? "Leaderboard belum bisa dimuat. Coba buka lagi sebentar lagi, ya."
            : "Leaderboard belum tersedia untuk kotamu. Cek lagi secara berkala, ya!"}
        </p>
      )}

      {!isLoading &&
        !isError &&
        rows.map((row, index) => (
          <LeaderboardRow
            key={row.id}
            rank={row.rank}
            schoolName={row.schoolName}
            isQualified={row.isQualified}
            isLast={index === rows.length - 1}
          />
        ))}
    </div>
  </div>
);

const FaqItem = ({ question, answer }) => (
  <details
    onToggle={(e) =>
      e.currentTarget.open &&
      analytics.sendEvent({ event: "faq_goldenticket", title: question })
    }
    className="group rounded-[20px] border-[1px] border-solid border-[#e7c75c] bg-white/90 p-[16px] shadow-[0px_6px_16px_-8px_rgba(201,166,43,0.07)]"
  >
    <summary className="flex cursor-pointer list-none items-center gap-[10px] [&::-webkit-details-marker]:hidden">
      <p className="m-0 min-w-0 flex-1 text-[14px] font-bold leading-[20px] text-[#1f2d3d]">
        {question}
      </p>
      <span className="flex size-[28px] shrink-0 items-center justify-center rounded-full bg-[#fff2be]">
        {/* Panah default-nya nunjuk ke atas (kondisi terbuka di Figma), jadi yang
            diputar justru keadaan tertutupnya. */}
        <ChevronUp className="size-[14px] rotate-180 transition-transform group-open:rotate-0" />
      </span>
    </summary>
    <div className="mt-[10px] h-px w-full bg-[#ebd994]" />
    {/* mb-0, bukan m-0: `m-0` dan `mt-*` sama-sama utility Tailwind buat properti
        margin, dan `m-0` yang menang — jarak atasnya jadi ilang. Landing juga
        pakai pola yang sama (mb-0 mt-[8px]). */}
    <p className="mb-0 mt-[10px] text-[13px] leading-[20px] text-[#6a7481]">
      {answer}
    </p>
  </details>
);

/**
 * Section leaderboard Golden Ticket di dashboard user.
 *
 * Cuma dirender buat kategori yang punya program ini (SMP & SMA). Kategori lain
 * ga cuma disembunyikan tampilannya — endpoint-nya juga ga dipanggil sama sekali,
 * karena buat mereka datanya emang ga ada.
 */
const GoldenTicketSection = ({ schoolCategory, cityName, schoolName }) => {
  const category = categoryParam(schoolCategory);
  // Waktu halaman dibuka, dikunci sekali: cukup buat nandain kapan screenshot
  // diambil. Ga dibuat jalan tiap menit — re-render terus demi detik ga sepadan.
  const [openedAt] = React.useState(() => formatWatermarkTime(new Date()));

  const {
    data: dataRaw,
    isFetching,
    isError,
  } = useFetchGoldenTicketSchools(category);
  console.log("dataRaw", dataRaw, category, schoolCategory, cityName);
  if (!category) return null;

  const payload = dataRaw?.data;
  const rows = toLeaderboardRows(payload?.data);
  const lastUpdated = formatLastUpdated(payload?.last_updated_at);
  const categoryLabel = category.toUpperCase();
  // Watermark: sekolah yang lihat, kapan dilihat, dan data per kapan — satu info
  // satu baris, karena digabung satu kalimat lebarnya ngelewatin kartu mobile.
  // Tanpa nama sekolah ga dipasang: jam doang ga nunjuk ke siapa-siapa.
  const watermark = schoolName
    ? watermarkBackground(
        [
          schoolName,
          `Diakses: ${openedAt}`,
          lastUpdated && `Leaderboard Diperbarui: ${lastUpdated}`,
        ].filter(Boolean),
      )
    : null;

  return (
    <section className="relative w-full overflow-hidden rounded-[24px] border-[1px] border-solid border-[#e7d38a] bg-gradient-to-r from-[#fff9e8] to-[#fff3bf] p-[16px] shadow-[0px_2px_8px_0px_rgba(0,0,0,0.03),0px_10px_24px_-10px_rgba(201,166,43,0.12)] lg:p-[40px]">
      <EllipseTop className="pointer-events-none absolute -right-[55px] -top-[49px] size-[176px]" />
      <EllipseBottom className="pointer-events-none absolute -bottom-[47px] -left-[37px] size-[132px]" />

      <div className="relative flex flex-col gap-[24px]">
        <div className="flex flex-col items-start gap-[24px] lg:flex-row lg:items-center">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-[16px]">
            <span className="rounded-[4px] bg-[#1f2d3d] px-[4px] py-[2px] text-[11px] font-bold leading-[13px] text-white">
              Golden Ticket
            </span>
            <div className="flex flex-col gap-[12px] text-[#1f2d3d]">
              <h2 className="m-0 text-[20px] font-bold leading-[28px] tracking-[0.1px] lg:text-[28px] lg:leading-[37px]">
                AMANKAN GOLDEN TICKET, BUKTIKAN DI LAPANGAN!
              </h2>
              <p className="m-0 text-[13px] leading-[20px]">
                Kejar peringkat teratas di leaderboard Golden Ticket kotamu dan
                dapatkan kesempatan melaju ke babak kualifikasi Piala by.U 2026
                tanpa biaya pendaftaran!
              </p>
            </div>
          </div>

          {/* Slot Figma 268×134 (2:1), gambarnya sedikit lebih tinggi (689×376) —
              object-cover motong padding transparan atas-bawahnya, sama kayak di
              Figma. Mobile diperkecil ke 240 biar aman di layar 320px. */}
          <div className="shrink-0 self-center p-[10px]">
            <img
              src={getStaticImg("golden-ticket/ticket.webp")}
              alt="Golden Ticket Piala by.U 2026"
              className="aspect-[2/1] w-[240px] rounded-[20px] object-cover lg:w-[268px]"
            />
          </div>
        </div>

        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col items-start gap-[8px] lg:flex-row lg:items-center lg:justify-between">
            <p className="m-0 text-[16px] font-bold leading-[24px] tracking-[0.08px] text-[#1f2d3d]">
              Leaderboard {categoryLabel}: Piala by.U {cityName}
            </p>
            {/* Tetap tampil walau tanggalnya belum ada — labelnya sendiri ngasih
                tau data ini emang diperbarui berkala, cuma belum ada isinya. */}
            <span className="rounded-full border-[1px] border-solid border-[#e7d38a] bg-[#fff8e8] px-[12px] py-[8px] text-[13px] leading-[20px] text-[#6a7481]">
              Terakhir diperbarui: {lastUpdated || "-"}
            </span>
          </div>

          <Leaderboard
            isLoading={isFetching}
            isError={isError}
            rows={rows}
            watermark={watermark}
          />
        </div>

        <div className="flex flex-col items-start gap-[16px] rounded-[24px] border-[1px] border-solid border-[rgba(248,207,90,0.4)] bg-gradient-to-b from-[#f2b61a] to-[#c97d33] p-[24px] shadow-[0px_10px_12px_rgba(0,0,0,0.1)] lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-[8px]">
            <p className="m-0 text-[16px] font-bold leading-[24px] tracking-[0.08px] text-white">
              Siap bawa sekolahmu berlaga di Piala by.U 2026?
            </p>
            <p className="m-0 text-[13px] leading-[20px] text-white/80">
              Hubungi tim kami lewat WhatsApp untuk pertanyaan lebih lanjut
              tentang Program Golden Ticket.
            </p>
          </div>
          <a
            href={GOLDEN_TICKET_WA}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-[44px] w-full max-w-[327px] items-center justify-center rounded-[40px] bg-white px-[16px] text-[16px] font-bold leading-[24px] tracking-[0.08px] no-underline lg:w-[220px]"
          >
            <span className="bg-gradient-to-b from-[#f2b61a] to-[#c97d33] bg-clip-text text-transparent">
              Hubungi via WhatsApp
            </span>
          </a>
        </div>

        <div className="flex flex-col gap-[16px]">
          <p className="m-0 text-[16px] font-bold leading-[24px] tracking-[0.08px] text-[#1f2d3d]">
            FAQ Golden Ticket
          </p>
          <div className="flex flex-col gap-[12px]">
            {FAQS.map((faq) => (
              <FaqItem key={faq.id} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoldenTicketSection;
